import React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';

interface Props {
    icon: any;
    text: string;
    children: any;
    defaultOpen?: boolean;
    /**
     * Si es true, no fuerza altura ni scroll interno: el padre (p. ej. cuerpo de un diálogo) hace el scroll.
     * Por defecto false para conservar el comportamiento previo en listas y paneles con altura fija.
     */
    disableInternalScroll?: boolean;
    /**
     * Tarjeta con borde y cabecera con fondo (p. ej. acordeones dentro de un diálogo con scroll único).
     */
    dialogSection?: boolean;
}

const CollapseMenu = ({
    icon,
    text,
    children,
    defaultOpen = false,
    disableInternalScroll = false,
    dialogSection = false,
}: Props) => {
    const [expand, setExpand] = React.useState(defaultOpen);

    const rootScrollSx = disableInternalScroll
        ? {
              alignContent: 'start' as const,
              minHeight: 0,
              height: 'auto',
              overflow: 'visible' as const,
          }
        : {
              height: '100%',
              overflowY: 'auto' as const,
          };

    const collapseWrapperSx = disableInternalScroll
        ? {
              minHeight: 0,
              height: 'auto',
              overflow: 'visible' as const,
          }
        : {
              height: '100%',
              overflowY: 'auto' as const,
          };

    const expandIconSx = {
        transition: (theme: Theme) => theme.transitions.create(['transform']),
        ...(expand ? { transform: 'rotate(180deg)' } : {}),
    };

    const inner = (
        <Box
            sx={{
                display: 'grid',
                ...rootScrollSx,
            }}
        >
            <Box
                onClick={() => setExpand(!expand)}
                sx={
                    dialogSection
                        ? {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 2,
                              py: 1.25,
                              minHeight: 44,
                              cursor: 'pointer',
                              userSelect: 'none',
                              position: 'relative',
                              zIndex: 1,
                              bgcolor: 'action.hover',
                              borderBottom: (theme) =>
                                  expand ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
                          }
                        : {
                              display: 'grid',
                              gridTemplateColumns: '30px auto 30px',
                              alignItems: 'center',
                              p: 0.5,
                              pl: 0.8,
                              cursor: 'pointer',
                              userSelect: 'none',
                          }
                }
            >
                {dialogSection ? (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                                color: 'text.secondary',
                                '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                            }}
                        >
                            {icon}
                        </Box>
                        <Typography
                            component='div'
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                m: 0,
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                color: 'text.secondary',
                            }}
                        >
                            {text}
                        </Typography>
                        <ExpandMoreIcon
                            sx={{
                                flexShrink: 0,
                                color: 'action.active',
                                ...expandIconSx,
                            }}
                        />
                    </>
                ) : (
                    <>
                        {icon}
                        <Typography
                            sx={{
                                textTransform: 'uppercase',
                                fontWeight: '600',
                                fontSize: '0.8125rem',
                                color: 'rgba(0, 0, 0, 0.64)',
                            }}
                        >
                            {text}
                        </Typography>
                        <ExpandMoreIcon sx={expandIconSx} />
                    </>
                )}
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    ...collapseWrapperSx,
                }}
            >
                <Collapse in={expand} timeout='auto' unmountOnExit>
                    {dialogSection ? (
                        <Box
                            sx={{
                                px: 2,
                                pt: 2,
                                pb: 2,
                                boxSizing: 'border-box',
                            }}
                        >
                            {children}
                        </Box>
                    ) : (
                        children
                    )}
                </Collapse>
            </Box>
        </Box>
    );

    if (!dialogSection) {
        return inner;
    }

    return (
        <Box
            sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                position: 'relative',
                isolation: 'isolate',
            }}
        >
            {inner}
        </Box>
    );
};

export default CollapseMenu;
